import {
  Component,
  ViewEncapsulation,
  viewChild,
  input,
  ChangeDetectionStrategy,
  inject,
  linkedSignal,
  model,
  effect,
  output,
  computed,
  resource,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';

import { ClarityModule } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { FileForPreview } from '../../_shared/preview-thumbnails/preview-thumbnail.type';
import { errorHandle } from 'src/app/misc/functions';

export interface HilfeBeschreibung {
  HelpBeschreibung?: string;
  HelpBildBase64?: string;
  FileTyp?: string;
}

@Component({
  selector: 'app-hilfe-beschreibung-modal',
  templateUrl: './hilfebeschreibungmodal.component.html',
  styleUrls: ['./hilfebeschreibungmodal.component.scss'],
  imports: [ClarityModule, FormsModule, PreviewThumbnailsComponent, AutofocusDirective, MrTranslatePipe],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HilfeBeschreibungModalComponent {
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly apiService = inject(APIService);
  private readonly domSanitizer = inject(DomSanitizer);

  readonly isModalOpen = model.required<boolean>();
  readonly table = input.required<HelperTabelle>();
  readonly id = input.required<number>();
  readonly isReadOnly = input(false, { alias: 'readonly' });
  readonly onSave = output<HilfeBeschreibung>();

  private readonly preview = viewChild(PreviewThumbnailsComponent);

  private readonly helperResource = resource<HilfeBeschreibung, number>({
    params: () => (this.isModalOpen() ? this.id() : undefined),
    loader: ({ params }) => this.apiService.getHilfeBeschreibung(this.table(), params),
    defaultValue: {},
  });

  protected readonly helper = this.helperResource.value;
  protected readonly isLoading = this.helperResource.isLoading;
  protected readonly pdfUrl = linkedSignal<SafeUrl>(() =>
    this.helper().FileTyp?.toLowerCase() == 'pdf' ?
      this.domSanitizer.bypassSecurityTrustResourceUrl(`data:application/pdf;base64,${this.helper().HelpBildBase64}`)
    : null
  );
  protected readonly media = computed(() => {
    const { HelpBildBase64: base64, FileTyp: extension } = this.helper();
    const media: [FileForPreview] =
      base64 ? [{ base64, name: `${this.mrTranslate.transform('Hilfe')}.${extension}` }] : null;
    return media;
  });

  constructor() {
    effect(() => {
      if (
        this.isReadOnly() &&
        this.helperResource.status() == 'resolved' &&
        !this.helper().FileTyp &&
        !this.helper().HelpBeschreibung
      ) {
        this.toastr.warning(this.mrTranslate.transform('Keine Hilfebeschreibung vorhanden'));
        this.isModalOpen.set(false);
      }
    });
  }

  protected async saveHelper(text: string) {
    const [{ base64 = null, extension = null } = {}] = await this.preview().packUnsavedFiles();

    if (text == this.helper().HelpBeschreibung && base64 == this.helper().HelpBildBase64) {
      this.isModalOpen.set(false);
      return;
    }

    const sendObj: HilfeBeschreibung = {
      HelpBeschreibung: text || null, // get rid of ''
      HelpBildBase64: base64,
      FileTyp: extension,
    };

    try {
      await this.apiService.setHilfeBeschreibung(this.table(), this.id(), sendObj);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.onSave.emit(sendObj);
      this.isModalOpen.set(false);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected checkPDF(name: string) {
    if (name?.split('.').pop().toLowerCase() == 'pdf') this.preview().forceOpenPdf(0);
  }

  protected showPDF(file: File) {
    if (!this.pdfUrl()) this.pdfUrl.set(this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file)));
  }
  //#endregion
}

export type HelperTabelle = 'SMANG' | 'SZAEHN' | 'KWAART';
